import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PageContainer from "../../Components/Page/PageContainer";
import { MultiSelect } from "primereact/multiselect";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import { apiService } from "../../api/api";
import { useDispatch, useSelector } from "react-redux";
import { signIn } from "../../Features/Auth/AuthenticationSlice";
import { auth } from "../../Features/config/firebase";

// User interface matching backend response
interface User {
  email: string;
  name: string;
  role: string;
  tools: string[];
  active: boolean;
  lastUpdated: string;
}

// Legacy interface for display
interface DisplayUser {
  Name: string;
  Email: string;
  tools: string[];
  active: boolean;
}

interface ToolOption {
  label: string;
  value: string;
}

const toolsKey = (tools: string[]): string => [...tools].sort().join("|");

const parseTools = (tools: unknown): string[] => {
  if (Array.isArray(tools)) return tools;
  if (typeof tools === "string") {
    try {
      const parsed = JSON.parse(tools);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

type ActiveFilterOption = "active" | "inactive" | "all";

const AppManagment = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state: any) => state.googleAuth.user);
  const [users, setUsers] = useState<DisplayUser[]>([]);
  const [toolOptions, setToolOptions] = useState<ToolOption[]>([]);
  const [selectedTools, setSelectedTools] = useState<Record<string, string[]>>(
    {}
  );
  const [activeFilter, setActiveFilter] =
    useState<ActiveFilterOption>("active");
  const [loading, setLoading] = useState<boolean>(true);
  const [savingUsers, setSavingUsers] = useState<Set<string>>(new Set());
  const [syncingClaims, setSyncingClaims] = useState<boolean>(false);

  const toast = useRef<Toast>(null);

  const validToolValues = useMemo(
    () => new Set(toolOptions.map((option) => option.value)),
    [toolOptions]
  );

  // Users can still hold decommissioned tool values (e.g. "PEX"); the server
  // rejects them, so drop anything the server no longer advertises as valid.
  const sanitizeTools = useCallback(
    (tools: string[]): string[] =>
      validToolValues.size === 0
        ? tools
        : tools.filter((tool) => validToolValues.has(tool)),
    [validToolValues]
  );

  const onSelectionChange = useCallback(
    (email: string, selectedTools: string[]) => {
      setSelectedTools((prevSelectedTools) => ({
        ...prevSelectedTools,
        [email]: selectedTools,
      }));
    },
    []
  );

  const fetchValidTools = useCallback(async () => {
    try {
      const response = await apiService.users.getValidTools();
      setToolOptions(response.tools ?? []);
    } catch (error) {
      console.error("Error fetching valid tools:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load tool options.",
      });
    }
  }, []);

  const fetchUsers = useCallback(
    async (filter?: ActiveFilterOption) => {
      const value = filter ?? activeFilter;
      try {
        setLoading(true);
        const response = await apiService.users.getAllUsers(value);
        const transformedData = response.data.map((user: User) => {
          return {
            Name: user.name,
            Email: user.email,
            tools: parseTools(user.tools),
            active: user.active === true,
          } as DisplayUser;
        });

        setUsers(transformedData);

        const initialSelectedTools: Record<string, string[]> = {};
        transformedData.forEach((user: DisplayUser) => {
          initialSelectedTools[user.Email] = [...user.tools];
        });
        setSelectedTools(initialSelectedTools);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch users. Please refresh the page.",
        });
      } finally {
        setLoading(false);
      }
    },
    [activeFilter]
  );

  useEffect(() => {
    fetchValidTools();
  }, [fetchValidTools]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const changedEmails = useMemo(() => {
    const changed = new Set<string>();
    users.forEach((user) => {
      const baseline = sanitizeTools(user.tools);
      const pending = sanitizeTools(selectedTools[user.Email] ?? user.tools);
      if (toolsKey(baseline) !== toolsKey(pending)) {
        changed.add(user.Email);
      }
    });
    return changed;
  }, [users, selectedTools, sanitizeTools]);

  const toolSelectorTemplate = (rowData: DisplayUser) => {
    const email = rowData.Email;
    const isUserBeingSaved = savingUsers.has(email);
    const value = sanitizeTools(selectedTools[email] ?? rowData.tools);

    return (
      <div style={{ width: "260px", maxWidth: "260px" }}>
        <MultiSelect
          value={value}
          options={toolOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(e) => onSelectionChange(email, e.value ?? [])}
          onClick={(e) => e.stopPropagation()}
          placeholder="Select Tools"
          maxSelectedLabels={2}
          selectedItemsLabel={`${value.length} tools selected`}
          disabled={isUserBeingSaved}
          className="w-full"
          style={{ maxWidth: "100%" }}
        />
      </div>
    );
  };

  const saveUserTools = async (user: DisplayUser) => {
    const toolsToSave = sanitizeTools(selectedTools[user.Email] ?? user.tools);

    if (toolsKey(sanitizeTools(user.tools)) === toolsKey(toolsToSave)) {
      toast.current?.show({
        severity: "info",
        summary: "No Changes",
        detail: `No changes detected for ${user.Name}`,
      });
      return;
    }

    try {
      // Add user to saving set
      setSavingUsers((prev) => new Set(prev).add(user.Email));

      console.log("Saving user tools:", user.Email, toolsToSave);
      await apiService.users.updateUserTools(user.Email, toolsToSave);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: `${user.Name}'s permissions updated successfully!`,
      });

      // If the saved user is the currently logged-in user, force a token
      // refresh so their new claims are reflected immediately without re-login
      if (
        auth.currentUser &&
        user.Email.toLowerCase() === currentUser?.email?.toLowerCase()
      ) {
        const freshToken = await auth.currentUser.getIdToken(true);
        const idTokenResult = await auth.currentUser.getIdTokenResult();
        dispatch(
          signIn({
            displayName: auth.currentUser.displayName,
            email: auth.currentUser.email,
            photoURL: auth.currentUser.photoURL,
            uid: auth.currentUser.uid,
            roles: idTokenResult.claims,
          })
        );
        console.log("Token refreshed with new claims for", user.Email, freshToken ? "✓" : "✗");
      }

      // Refresh the user data to get the latest state
      await fetchUsers();
    } catch (error: any) {
      console.error("Error updating user tools:", error);
      const responseData = error?.response?.data;
      const validationErrors = Array.isArray(responseData?.errors)
        ? responseData.errors.join("; ")
        : "";
      const baseMessage =
        responseData?.message ||
        error?.message ||
        "Failed to update user permissions";
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: validationErrors
          ? `${baseMessage}: ${validationErrors}`
          : baseMessage,
      });
    } finally {
      // Remove user from saving set
      setSavingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user.Email);
        return newSet;
      });
    }
  };

  const saveButtonTemplate = (rowData: DisplayUser) => {
    const isUserBeingSaved = savingUsers.has(rowData.Email);
    const userHasChanges = changedEmails.has(rowData.Email);

    return (
      <Button
        label={isUserBeingSaved ? "Saving..." : "Save"}
        onClick={(e) => {
          e.stopPropagation();
          saveUserTools(rowData);
        }}
        disabled={isUserBeingSaved || !userHasChanges}
        loading={isUserBeingSaved}
        severity={userHasChanges ? "success" : "secondary"}
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <PageContainer HomeMenuBarprops={{ title: "User Management" }}>
        <div
          className="flex justify-content-center align-items-center"
          style={{
            minHeight: "400px",
            padding: "1.5rem 2rem 0 2rem",
          }}
        >
          <ProgressSpinner />
        </div>
      </PageContainer>
    );
  }

  const activeFilterOptions: { label: string; value: ActiveFilterOption }[] = [
    { label: "Active only", value: "active" },
    { label: "Inactive only", value: "inactive" },
    { label: "All users", value: "all" },
  ];

  const statusBodyTemplate = (rowData: DisplayUser) => (
    <Tag
      value={rowData.active ? "Active" : "Inactive"}
      severity={rowData.active ? "success" : "info"}
    />
  );

  const handleSyncClaims = async () => {
    try {
      setSyncingClaims(true);
      await apiService.users.syncAllUsersClaims();
      toast.current?.show({
        severity: "success",
        summary: "Sync started",
        detail:
          "Custom claims and active status are updating in the background. Refresh the list in a moment to see changes.",
      });
      await fetchUsers();
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "response" in error
          ? (error as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : error instanceof Error
            ? error.message
            : "Failed to start sync";
      toast.current?.show({
        severity: "error",
        summary: "Sync failed",
        detail: message,
      });
    } finally {
      setSyncingClaims(false);
    }
  };

  return (
    <PageContainer HomeMenuBarprops={{ title: "User Management" }}>
      <Toast ref={toast} />
      <div
        className="flex flex-column gap-3"
        style={{ padding: "1.5rem 2rem 0 2rem", maxWidth: "100%" }}
      >
        <div className="flex flex-wrap align-items-end gap-3 mb-2">
          <div className="flex flex-column gap-2">
            <label htmlFor="active-filter" className="font-medium">
              Show users
            </label>
            <SelectButton
              id="active-filter"
              value={activeFilter}
              options={activeFilterOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setActiveFilter(e.value)}
            />
          </div>
          <Button
            label={syncingClaims ? "Syncing…" : "Sync claims & active status"}
            icon="pi pi-sync"
            loading={syncingClaims}
            disabled={syncingClaims}
            onClick={handleSyncClaims}
            severity="secondary"
          />
        </div>
        <DataTable
          value={users}
          dataKey="Email"
          cellMemo={false}
          paginator
          filterDisplay="row"
          rows={10}
          loading={loading}
          emptyMessage="No users found"
          className="p-datatable-sm"
          tableStyle={{ tableLayout: "fixed", width: "100%" }}
        >
        <Column
          field="Name"
          header="Name"
          filter
          filterPlaceholder="Search by name"
          sortable
        />
        <Column
          field="Email"
          header="Email"
          filter
          filterPlaceholder="Search by email"
          sortable
        />
        <Column
          field="active"
          header="Status"
          body={statusBodyTemplate}
          sortable
          sortField="active"
        />
        <Column
          header="Tools"
          body={toolSelectorTemplate}
          style={{ width: "280px", maxWidth: "280px" }}
        />
        <Column
          header="Actions"
          body={saveButtonTemplate}
          style={{ minWidth: "100px" }}
        />
        </DataTable>
      </div>
    </PageContainer>
  );
};

export default AppManagment;
